/**
 * FixSeriesOrderPanel — Studio header pill that auto-detects multi-part
 * series (Balkans-06, Balkans-07, Chapter 5:, etc.) from their titles
 * and rewrites `created_at` on each so the RSS feed / public grid /
 * Spotify play them in numerical order (06 → 07 → 08).
 *
 * Uses POST /api/admin/auto-sort-series-by-chapter with `?dry_run=true`
 * first so the creator sees the plan before anything writes.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { ArrowUpDown, Loader2, X, Check, ChevronRight, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "storylo_admin_token";

const _adminToken = () => {
  let t = window.localStorage.getItem(ADMIN_TOKEN_KEY);
  if (!t) {
    t = window.prompt("Enter your admin token:");
    if (t) window.localStorage.setItem(ADMIN_TOKEN_KEY, t);
  }
  return t || "";
};

export default function FixSeriesOrderPanel({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState("");

  const openAndPreview = async () => {
    setOpen(true);
    setPlan(null);
    setApplied(false);
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/auto-sort-series-by-chapter?dry_run=true`,
        {},
        { headers: { "X-Admin-Token": _adminToken() } }
      );
      setPlan(data);
    } catch (e) {
      if (e?.response?.status === 403)
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
      setError(e?.response?.data?.detail || e.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    setError("");
    try {
      await axios.post(
        `${API}/admin/auto-sort-series-by-chapter`,
        {},
        { headers: { "X-Admin-Token": _adminToken() } }
      );
      setApplied(true);
      onApplied?.();
    } catch (e) {
      if (e?.response?.status === 403)
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
      setError(e?.response?.data?.detail || e.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <button
        onClick={openAndPreview}
        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-full border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5"
        data-testid="fix-series-order-open"
        title="Auto-detect multi-part series and reorder them 06 → 07 → 08 for Spotify / RSS / Studio"
      >
        <ArrowUpDown className="w-3.5 h-3.5" />
        Fix series order
      </button>
      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-8 md:pt-12 pb-8 overflow-y-auto"
          data-testid="fix-series-order-modal"
          onClick={(e) => e.target === e.currentTarget && !applying && setOpen(false)}
        >
          <div className="panel max-w-3xl w-full max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col my-auto">
            <div className="flex items-start justify-between p-6 border-b border-white/5">
              <div>
                <div className="label-eyebrow mb-1 flex items-center gap-2">
                  <ArrowUpDown className="w-3.5 h-3.5" /> Fix series order
                </div>
                <div className="font-display text-lg">
                  Re-sequence multi-part series by chapter number
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed">
                  Detects titles like <em>Balkans-06-…</em>, <em>Chapter 5:</em>,
                  <em> Twice Built 03 —</em>, etc. and rewrites pub-dates so each
                  project plays as one uninterrupted block on Spotify — every
                  chapter in order (06 → 07 → 08 …) with no other stories in between.
                </div>
              </div>
              <button
                onClick={() => !applying && setOpen(false)}
                className="text-[color:var(--muted)] hover:text-white"
                data-testid="fix-series-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loading && (
                <div className="flex items-center justify-center py-16 text-[color:var(--muted)]">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Reading your library…
                </div>
              )}
              {error && (
                <div className="text-xs text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              {plan && !loading && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <StatBox label="Series detected" value={plan.series_detected} />
                    <StatBox
                      label="Episodes to reorder"
                      value={plan.episodes_reordered}
                      tint="emerald"
                    />
                    <StatBox
                      label="Unmatched (skipped)"
                      value={plan.unmatched_count}
                      tint="amber"
                    />
                  </div>

                  {plan.series.length === 0 && (
                    <div className="panel p-6 text-center text-sm text-[color:var(--muted)]">
                      No multi-part series detected. Titles need to include a chapter number
                      (e.g. <em>Balkans-06-…</em>) for auto-detection to work.
                    </div>
                  )}

                  {plan.series.map((g) => (
                    <div
                      key={g.series}
                      className="panel p-4"
                      data-testid={`series-plan-${g.series}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium capitalize">
                          {g.series}{" "}
                          <span className="text-xs text-[color:var(--muted)]">
                            · {g.count} episodes
                          </span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {g.items.map((it) => (
                          <div
                            key={it.id}
                            className="flex items-center gap-2 text-xs py-1 border-t border-white/5 first:border-t-0"
                          >
                            <span className="w-8 text-[color:var(--accent)] font-medium">
                              {String(it.chapter).padStart(2, "0")}
                            </span>
                            <span className="flex-1 truncate" title={it.title}>
                              {it.title}
                            </span>
                            <ChevronRight className="w-3 h-3 opacity-40" />
                            <span className="text-[10px] text-[color:var(--muted)] whitespace-nowrap">
                              pos #{it.position + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {plan.unmatched_count > 0 && (
                    <div className="rounded-lg border border-white/10 p-3 text-xs text-[color:var(--muted)]">
                      <div className="flex items-center gap-1.5 text-amber-300 mb-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {plan.unmatched_count} stand-alone titles skipped
                      </div>
                      Non-series or single-episode titles are left in their current
                      relative position (they may shift out of a series block so
                      chapters stay together). If you expected an episode here,
                      rename it to include a chapter number (e.g. add <em>-06-</em>)
                      then run again.
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-white/5">
              <button
                onClick={() => !applying && setOpen(false)}
                className="text-xs px-3 py-2 rounded-lg border border-white/10 hover:border-white/25"
                data-testid="fix-series-cancel"
              >
                Close
              </button>
              {plan && plan.series_detected > 0 && !applied && (
                <button
                  onClick={apply}
                  disabled={applying}
                  className="btn-primary text-xs inline-flex items-center gap-1.5 px-4 py-2 disabled:opacity-40"
                  data-testid="fix-series-apply"
                >
                  {applying ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" /> Apply reorder
                    </>
                  )}
                </button>
              )}
              {applied && (
                <span
                  className="text-xs text-emerald-400 inline-flex items-center gap-1"
                  data-testid="fix-series-applied"
                >
                  <Check className="w-3.5 h-3.5" /> Reorder saved · Spotify picks it up on
                  the next feed poll (~15–30 min)
                </span>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function StatBox({ label, value, tint }) {
  const color =
    tint === "emerald"
      ? "text-emerald-400"
      : tint === "amber"
      ? "text-amber-300"
      : "text-white";
  return (
    <div className="panel p-3">
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}
